import { useCallback, useEffect, useState } from 'react'
import { Button, Field, ListSkeleton, Skeleton } from '../components/ui'
import { api } from '../lib/api'
import { planPrice, money, birr, calendarDate, daysUntil, relativeTime } from '../lib/format'
import { useToast } from '../lib/toast'
import { useApi } from '../lib/useApi'
import { Link, useSearchParams } from 'react-router-dom'
import PaymentReturn from '../components/PaymentReturn'
import ReceiptForm from '../components/ReceiptForm'
import { billingReturnLabel, safeBillingReturn } from '../lib/billingFlow'
import PlanQuote from '../components/PlanQuote'
import type { Plan, PlanTier, StatementLine } from '../lib/types'

const PRESETS = [200, 500, 1000, 2500]

const PROVIDER_LABEL: Record<string, string> = {
  chapa: 'Chapa',
  telebirr: 'Telebirr',
  cbe_birr: 'CBE Birr',
  stripe: 'Card',
  paypal: 'PayPal',
}

export default function Billing() {
  const { data: wallet, error: walletError, reload: reloadWallet } = useApi(() => api.wallet(), [], 30_000)
  const { data: transactions, error: transactionsError, reload: reloadTransactions } = useApi(() => api.transactions(), [])
  const { data: plans, error: plansError } = useApi(() => api.plans(), [])
  const [params, setParams] = useSearchParams()
  const [adding, setAdding] = useState(params.get('add') === '1')
  const [currency, setCurrency] = useState(params.get('currency') === 'USD' ? 'USD' : 'ETB')
  const [currencyChosen, setCurrencyChosen] = useState(params.has('currency'))
  const [amount, setAmount] = useState(params.get('amount') && Number(params.get('amount')) > 0 ? String(Math.max(currency === 'USD' ? 1 : 50, Number(params.get('amount')))) : currency === 'USD' ? '10' : '500')
  useEffect(() => {
    if (wallet && !currencyChosen) {
      setCurrency(wallet.currency)
      if (!params.has('amount')) setAmount(wallet.currency === 'USD' ? '10' : '500')
    }
  }, [wallet, currencyChosen, params])
  const [provider, setProvider] = useState('stripe')
  const [phone, setPhone] = useState('')
  const [busy, setBusy] = useState(false)
  const [revision, setRevision] = useState(0)
  const [checkoutError, setCheckoutError] = useState('')
  const { attempt } = useToast()
  const paymentReference = params.get('payment')
  const returnTo = safeBillingReturn(params.get('returnTo')) || safeBillingReturn(sessionStorage.getItem('billing-return'))
  useEffect(() => {
    const next = safeBillingReturn(params.get('returnTo'))
    if (next) sessionStorage.setItem('billing-return', next)
    if (params.get('add') === '1') {
      const requestedCurrency = params.get('currency') === 'USD' ? 'USD' : 'ETB'
      const requestedAmount = Number(params.get('amount'))
      setAdding(true)
      if (params.has('currency')) { setCurrency(requestedCurrency); setCurrencyChosen(true) }
      if (Number.isFinite(requestedAmount) && requestedAmount > 0) setAmount(String(Math.max(requestedCurrency === 'USD' ? 1 : 50, requestedAmount)))
    }
  }, [params])
  const refreshPayment = useCallback(() => { reloadWallet(); reloadTransactions(); setRevision(n => n + 1) }, [reloadWallet, reloadTransactions])
  const providers = (wallet?.providers ?? []).filter(p => currency === 'USD' ? ['stripe', 'paypal'].includes(p) : !['stripe', 'paypal'].includes(p))
  const methods = [...providers, ...(currency === 'ETB' && wallet?.manual_topups ? ['bank'] : [])]
  const selected = methods.includes(provider) ? provider : methods[0] ?? ''
  const minimum = currency === 'USD' ? 1 : 50
  const validAmount = Number.isFinite(Number(amount)) && Number(amount) >= minimum && (currency !== 'USD' || Number(amount) <= 10000)
  const topUp = async () => {
    if (busy || !validAmount) return
    setBusy(true); setCheckoutError('')
    try {
      const checkout = currency === 'USD'
        ? await api.internationalCheckout((await api.topUpQuote(amount, selected)).quote)
        : await api.topUp(amount, selected, phone || undefined)
      window.location.href = checkout.checkout_url
    } catch (e) { setCheckoutError(e instanceof Error ? e.message : 'Could not open checkout. Please retry.') }
    finally { setBusy(false) }
  }
  const onTrial = (daysUntil(wallet?.trial_ends_at) ?? 0) > 0
  return <>
    <header className="page-head"><div><h1>Billing</h1><p className="subtitle">Add money. Use your balance for plans and upgrades.</p></div>
      <Button variant="primary" disabled={!wallet} onClick={() => { setAdding(v => !v); if (adding) setParams(current => { current.delete('add'); return current }) }}>{adding ? 'Close top-up' : 'Add money'}</Button></header>
    {walletError && <div className="alert error">{walletError}</div>}
    {paymentReference && <PaymentReturn key={paymentReference} reference={paymentReference} canceled={params.get('canceled') === '1'} onConfirmed={refreshPayment} />}
    {returnTo && <div className="row"><Link className="btn" to={returnTo} onClick={() => sessionStorage.removeItem('billing-return')}>{billingReturnLabel(returnTo).label}</Link><span className="tiny muted">{billingReturnLabel(returnTo).hint}</span></div>}
    <section className="grid-3" aria-label="Wallet balances">
      {(['ETB', 'USD'] as const).map(c => <div className="card stat" key={c}><span className="stat-label">{c === 'ETB' ? 'Birr balance' : 'US dollar balance'}</span><strong className="stat-value">{wallet ? money(wallet.balances?.[c] ?? '0', c) : <Skeleton width={100} height={28} />}</strong><span className="tiny muted">{wallet?.currency === c ? 'Used for plans and upgrades' : 'Available to use'}</span></div>)}
      <div className="card stat"><span className="stat-label">Monthly plans</span><strong className="stat-value">{wallet ? money(wallet.monthly_burn, wallet.currency) : '—'}</strong><span className="tiny muted">{onTrial ? `Trial ends ${calendarDate(wallet?.trial_ends_at)}` : wallet?.next_charge_at ? `Next charge ${calendarDate(wallet.next_charge_at)}` : 'No upcoming charge'}</span></div>
    </section>
    {wallet?.low_balance && <p className="alert warn">Add funds to your {wallet.currency} wallet to cover upcoming renewals.</p>}
    {adding && <section className="card card-pad-lg stack" aria-labelledby="add-money-title">
      <div><h2 id="add-money-title">Add money</h2><p className="muted">Choose a currency and payment method.</p></div>
      <div className="field-row"><Field label="Wallet to top up" htmlFor="topup-currency"><select id="topup-currency" value={currency} disabled={busy} onChange={e => { setCurrencyChosen(true); setCurrency(e.target.value); setAmount(e.target.value === 'USD' ? '10' : '500'); setCheckoutError('') }}><option value="USD">US dollars (USD)</option><option value="ETB">Ethiopian Birr (ETB)</option></select></Field>
      <Field label="Payment method" htmlFor="payment-method"><select id="payment-method" value={selected} disabled={busy || !methods.length} onChange={e => { setProvider(e.target.value); setCheckoutError('') }}>{methods.map(m => <option key={m} value={m}>{m === 'bank' ? 'Bank deposit (CBE)' : PROVIDER_LABEL[m] ?? m}</option>)}</select></Field></div>
      {!methods.length && <p className="muted">No payment method is available for this currency yet. <Link to="/support">Contact Support</Link>.</p>}
      {selected === 'bank' ? <>
        {wallet?.bank_deposit && <div className="billing-bank"><p className="tiny muted">{wallet.bank_deposit.bank}</p><strong className="mono">{wallet.bank_deposit.account}</strong><p>{wallet.bank_deposit.holder}</p><Button size="sm" onClick={() => void attempt(() => navigator.clipboard.writeText(wallet.bank_deposit!.account), { success: 'Account number copied' })}>Copy account number</Button></div>}
        <p className="muted">Deposit to this account, then submit your receipt or reference below. Support verifies the deposit before crediting your Birr wallet.</p>
        <ReceiptForm />
      </> : selected && <form className="stack" onSubmit={e => { e.preventDefault(); void topUp() }}>
        <Field label={`Amount (${currency})`} htmlFor="topup-amount"><input id="topup-amount" type="number" min={minimum} max={currency === 'USD' ? 10000 : undefined} step="0.01" required disabled={busy} value={amount} onChange={e => setAmount(e.target.value)} /></Field>
        <div className="row">{(currency === 'USD' ? [5, 10, 25, 50] : PRESETS).map(n => <Button key={n} type="button" disabled={busy} onClick={() => setAmount(String(n))}>{money(n, currency)}</Button>)}</div>
        {selected === 'telebirr' && <Field label="Telebirr phone number" htmlFor="topup-phone"><input id="topup-phone" value={phone} required disabled={busy} onChange={e => setPhone(e.target.value)} placeholder="09xxxxxxxx" /></Field>}
        <p className="tiny muted">Your {currency} wallet receives {validAmount ? money(amount, currency) : 'the amount you pay'}. Provider fees may apply. Top-ups do not change your plan automatically.</p>
        {checkoutError && <p className="alert error" role="alert">{checkoutError}</p>}
        <Button type="submit" variant="primary" loading={busy} disabled={!validAmount}>Continue to secure checkout</Button>
      </form>}
    </section>}
    {wallet && <details className="card stack"><summary>Payment preferences · {wallet.currency} wallet</summary>
      <Field label="Use this wallet for plans and upgrades" htmlFor="billing-currency"><select id="billing-currency" value={wallet.currency} disabled={busy} onChange={async e => {
        const next = e.target.value; setBusy(true)
        await attempt(async () => { await api.billingCurrency(next); refreshPayment() }, { success: `Plans and upgrades now use your ${next} wallet`, failure: 'Could not change wallet' })
        setBusy(false)
      }}><option value="ETB">Birr (ETB)</option><option value="USD">US dollars (USD)</option></select></Field>
      <p className="tiny muted">Top-ups stay in their original currency. Balances are not combined or converted.</p>
    </details>}
    <Statement key={`${revision}-${params.get('upgrade')}-${params.get('plan')}`} plans={plans ?? null} />
    <details className="card stack"><summary>Compare all plans</summary>{plansError && <p className="alert error">{plansError}</p>}<div className="grid">{plans?.map(p => <div className="card" key={p.tier}><h3>{p.label}</h3><p>{Number(p.monthly_etb) === 0 ? 'Free' : `${planPrice(p)}/month`}</p><p className="tiny muted">{p.cpu} CPU · {p.memory} RAM{p.custom_domains ? ' · Custom domains' : ''}</p></div>)}</div></details>
    <section className="stack"><h2 className="section-title">Transaction history</h2>
      {transactionsError ? <p className="alert error">{transactionsError}</p> : !transactions ? <ListSkeleton rows={3} /> : transactions.length === 0 ? <p className="muted">Your top-ups and plan charges will appear here.</p> : <div className="list">{transactions.map(tx => <div className="list-item" key={tx.id}><div className="grow"><div>{tx.description}</div><span className="tiny muted">{relativeTime(tx.created_at)}</span></div><strong className="tabular">{Number(tx.amount) > 0 ? '+' : ''}{money(tx.amount, tx.currency ?? 'ETB')}</strong></div>)}</div>}
    </section>
  </>
}

/** What the next round of renewals will take, for what, and whether the
 * wallet covers it — with the plan changeable on the line itself.
 *
 * One line per application, however many components it has: the frontend,
 * the API and the worker are one price. A line that costs nothing says why,
 * because a zero with no reason reads as a mistake. The quote for a change
 * comes from the server, from the function that will do the charging.
 */
function Statement({ plans }: { plans: Plan[] | null }) {
  const { data, error, reload } = useApi(() => api.statement(), [], 30_000)
  if (error) return <section className="stack"><h2 className="section-title">Your applications</h2><div className="alert error">{error}</div></section>
  if (!data) return <section className="stack"><h2 className="section-title">Your applications</h2><ListSkeleton rows={2} /></section>
  const applications = data.lines.filter((line) => line.kind === 'application')
  const databases = data.lines.filter((line) => line.kind === 'database')
  const total = Number(data.monthly_total)
  const short = Number(data.shortfall)
  return (
    <section className="stack">
      <h2 className="section-title">Your applications</h2>
      {total > 0 ? (
        <p className="muted">
          Your next round of renewals takes <strong>{money(data.monthly_total, data.currency ?? 'ETB')}</strong>
          {data.next_charge_at && <>, the first on {calendarDate(data.next_charge_at)}</>}.{' '}
          {short > 0 ? (
            <span className="danger">Your balance is short by {money(data.shortfall, data.currency ?? 'ETB')}.</span>
          ) : (
            <>Your balance covers {data.renewals_covered} {data.renewals_covered === 1 ? 'round' : 'rounds'} of it.</>
          )}
        </p>
      ) : (
        <p className="muted">Nothing is being charged right now.</p>
      )}
      {data.lines.length === 0 && <p className="tiny muted">No applications or databases yet.</p>}
      <div className="statement">
        {applications.map((line) => (
          <StatementRow key={line.id} line={line} plans={plans} onChanged={reload} />
        ))}
        {databases.map((line) => (
          <StatementRow key={line.id} line={line} plans={plans} onChanged={reload} />
        ))}
      </div>
    </section>
  )
}

function StatementRow({ line, plans, onChanged }: { line: StatementLine; plans: Plan[] | null; onChanged: () => void }) {
  const [params] = useSearchParams()
  const requested = params.get('upgrade') === line.id ? params.get('plan') : null
  const [choice, setChoice] = useState<PlanTier>((plans?.some(p => p.tier === requested) ? requested : line.plan) as PlanTier)
  const [quote, setQuote] = useState<import('../lib/types').PlanQuote | null>(null)
  const [busy, setBusy] = useState(false)
  const { attempt } = useToast()
  const isApplication = line.kind === 'application'
  const href = isApplication ? `/projects/${line.id}` : `/databases/${line.id}`
  const selectable = (plans ?? []).filter((plan) => plan.tier !== 'free')
  const parts = isApplication
    ? [
        line.billed_components > 0 && `${line.billed_components} ${line.billed_components === 1 ? 'component shares' : 'components share'} the pool`,
        line.included_components > 0 && `${line.included_components} static ${line.included_components === 1 ? 'site' : 'sites'} included free`,
      ].filter(Boolean).join(' · ')
    : 'Managed database — its own plan, changed on its page'
  const change = async () => {
    setBusy(true)
    const result = await attempt(() => api.changeProjectPlan(line.id, choice), {
      success: 'Application plan updated — running components are being re-applied',
      failure: 'Could not change the plan',
    })
    setBusy(false)
    if (result) onChanged()
  }
  return (
    <div className="card statement-row" id={`application-${line.id}`}>
      <div className="statement-main">
        <div className="statement-name">
          <span className="platform-role">{isApplication ? 'Application' : 'Database'}</span>
          <Link to={href}><strong>{line.name}</strong></Link>
          <span className="tiny muted">{parts}</span>
        </div>
        <div className="statement-price">
          {line.why_free ? (
            <>
              <strong>Free</strong>
              <span className="tiny muted">{line.why_free} · {line.plan_label} lists at {birr(line.list_price)}/mo</span>
            </>
          ) : (
            <>
              <strong className="tabular">{planPrice(line)}<span className="tiny muted">/mo</span></strong>
              <span className="tiny muted">
                {line.plan_label}
                {line.paid_until ? ` · paid until ${calendarDate(line.paid_until)}` : ' · charged at the next renewal'}
              </span>
            </>
          )}
        </div>
      </div>
      {isApplication && !line.custom_domains_allowed && <Link className="btn primary" to={`/projects/${line.id}`}>Upgrade for custom domains</Link>}
      {isApplication && (line.billed_components > 0 || line.paid_features_enabled) && selectable.length > 0 && (
        <details className="statement-change stack" open={requested ? true : undefined}><summary>Change plan</summary>
          <label className="tiny muted" htmlFor={`plan-${line.id}`}>
            Plan
            <select id={`plan-${line.id}`} value={choice} onChange={(event) => setChoice(event.target.value as PlanTier)}>
              {!selectable.some(plan => plan.tier === line.plan) && <option value={line.plan} disabled>{line.plan_label} (current)</option>}
              {selectable.map((plan) => (
                <option key={plan.tier} value={plan.tier}>
                  {plan.label} — {plan.cpu} CPU, {plan.memory} · {planPrice(plan)}/mo
                </option>
              ))}
            </select>
          </label>
          <PlanQuote kind="project" id={line.id} plan={choice} current={line.plan} onQuote={setQuote} />
          {choice !== line.plan && (
            <div className="row">
              <Button variant="primary" loading={busy} disabled={!quote || quote.new_plan !== choice || !quote.affordable} onClick={change}>
                Switch to {selectable.find((plan) => plan.tier === choice)?.label ?? choice}
              </Button>
              <Button disabled={busy} onClick={() => setChoice(line.plan)}>Keep {line.plan_label}</Button>
            </div>
          )}
        </details>
      )}
    </div>
  )
}
